import { useSearchBox } from 'react-instantsearch';
import { Input, InputGroup, Button, HStack } from '@chakra-ui/react';
import {
  ChangeEvent,
  FormEvent,
  forwardRef,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CloseIcon, SearchIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useLocation } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';

type SearchInputProps = {
  onFocus?: () => void;
  onBlur?: () => void;
  onSubmit: () => void;
};

export const ModalSearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onFocus, onBlur, onSubmit }: SearchInputProps, ref): ReactElement => {
    const { pathname } = useLocation();
    const { query, refine, clear } = useSearchBox();
    const [inputValue, setInputValue] = useState<string>(query);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const debounceDelayInMs = 100;
    const showClearButton = inputValue.length !== 0;

    const handleSubmit = async (
      e: FormEvent<HTMLFormElement> | FormEvent<HTMLButtonElement>
    ): Promise<void> => {
      e.preventDefault();

      refine(query);
      onSubmit();
    };

    const handleDebouncedChange = (e: ChangeEvent<HTMLInputElement>): void => {
      e.preventDefault();
      const { value } = e.currentTarget;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => refine(value), debounceDelayInMs);

      setInputValue(value);
    };

    const handleCrossClick = (): void => {
      setInputValue('');
      clear();
    };

    const handleFocus = (): void => {
      refine(inputValue);
      if (onFocus) onFocus();
    };

    // Clear input field when navigating away from search page
    useEffect(() => {
      if (pathname !== ProtectedRoutes.SEARCH) {
        setInputValue('');
      }
    }, [pathname]);

    return (
      <form autoComplete="off" onSubmit={handleSubmit}>
        <HStack gap="4">
          <InputGroup
            startElement={<NovaIcon icon={SearchIcon} size="s" color="icon.quaternary" aria-hidden />}
            endElement={
              showClearButton && (
                <Button
                  onClick={handleCrossClick}
                  bg="transparent"
                  _hover={{ background: 'transparent' }}
                  variant="tertiary"
                >
                  <NovaIcon icon={CloseIcon} size="s" color="icon.quaternary" aria-hidden />
                </Button>
              )
            }
          >
            <Input
              ref={ref}
              bg="background.primary"
              px="4"
              py="2"
              border="default"
              borderColor="border.accent"
              borderRadius="10px"
              placeholder="Fachbegriff, Schlagwort, Medikament, Pflegemaßnahme"
              value={inputValue}
              onChange={handleDebouncedChange}
              onFocus={handleFocus}
              onBlur={onBlur}
              _placeholder={{ fontSize: '14px', fontWeight: '300px' }}
              _focus={{
                borderColor: 'border.accent',
              }}
              // Limit should be below 512 characters as we hit instantsearch API limits otherwise
              maxLength={400}
            />
          </InputGroup>
        </HStack>
      </form>
    );
  }
);
